import { useAuthenticatedUser } from "@/hooks/use-authenticated-user";
import { useBusinessReportAdmin } from "@/hooks/use-business-report-admin";
import { useDocumentAuthoringAccess } from "@/hooks/use-document-authoring-access";
import { isAuthoringTemplate, type BusinessReportSummary } from "@/modules/snapshots/businessReports.types";

/**
 * Who may do what in the template library (mirrors `pf/business_reports/api/template_access.py`).
 *
 * - Library admins create, edit, hide and delete every template of the organization.
 * - Authoring users (organization feature `document_authoring`, no admin role) create private
 *   authoring templates and manage only the authoring templates they own.
 * - Anyone who can see an authoring template may duplicate it into a private copy of their own.
 */
export const useTemplateLibraryAccess = () => {
  const isAdmin = useBusinessReportAdmin();
  const { hasDocumentAuthoring } = useDocumentAuthoringAccess();
  const { id: userId } = useAuthenticatedUser();
  const canAuthor = isAdmin || hasDocumentAuthoring;

  const canManage = (template: BusinessReportSummary): boolean =>
    isAdmin || (hasDocumentAuthoring && isAuthoringTemplate(template) && template.owner_id === userId);

  const canDuplicate = (template: BusinessReportSummary): boolean => canAuthor && isAuthoringTemplate(template);

  return { isAdmin, canAuthor, userId, canManage, canDuplicate };
};
